/**
 * A bearer-authenticated Server-Sent-Events stream over `fetch`, with its
 * own reconnect.
 *
 * Not `EventSource`, which cannot set a request header, so the token would
 * have to travel in the query string, where it would sit in load balancer
 * access logs and browser history long after it expires. This reads the
 * response body instead.
 *
 * Shared by both ends of the second-screen feature: the desktop's stream in
 * `useSecondScreenSession` and the phone's in `useSecondScreenHost`. The
 * phone needs the reconnect here more than the desktop does, since its
 * stream drops every time the screen locks.
 *
 * This mirrors `src/chat/api/eventStream.ts` in the htp-audience-layer
 * widget repo, which solves the same problem for the chat/presence stream:
 * same retry shape, same jitter, same reasoning for both. It is not
 * imported from here directly (a separate package, no shared node_modules
 * between the two repos), so this is a smaller, second-screen-scoped copy
 * of that shape rather than a shared dependency.
 */

export interface SecondScreenStreamEvent {
  event: string;
  data: any;
}

export interface SecondScreenStreamOptions {
  url: string;
  /**
   * Returns the current bearer token. Called fresh on every connection
   * attempt, including reconnects, rather than captured once: a plain
   * string would go stale the moment a caller rotates its token between
   * attempts, which is exactly what happened when the phone's route (see
   * useSecondScreenHost) adopted this helper without also adopting this
   * contract. Passing `() => tokenRef.current` (or the desktop's own ref) is
   * what makes a rotated token correct by construction rather than by luck.
   */
  getJwt: () => string;
  onEvent: (event: SecondScreenStreamEvent) => void;
  /** Told when the stream goes up or comes down, for anything that shows it. */
  onConnectedChange?: (connected: boolean) => void;
  /**
   * Told when the stream stops trying for good (401, 403 or 404: see the
   * terminal-status check below), so the caller can treat the session as
   * dead rather than sit on a layer that looks alive while the server 401s
   * every attempt indefinitely.
   */
  onTerminal?: (status: number) => void;
  /**
   * Renews the caller's credential and hands back the new one, or null.
   *
   * A 401 has two very different causes, and only one of them is terminal.
   * The session may genuinely be over, OR the token simply aged out: a pairing
   * lasts as long as both devices stay connected, hours for a game, while the
   * tokens on both ends are good for minutes. Without this, the second case
   * ends a perfectly live pairing and tells the fan to reload the page.
   *
   * Tried once per successful connection, so a token that is truly dead still
   * reaches onTerminal on the following attempt rather than looping.
   *
   * Optional: a caller with no way to renew (a test, any surface that has not
   * wired one up) keeps the previous behaviour exactly.
   */
  refreshAuth?: () => Promise<string | null>;
}

/** First wait after a stream drops. Doubles each attempt up to the cap. */
const RETRY_BASE_MS = 1_000;
const RETRY_MAX_MS = 30_000;

/**
 * The server sends a `:\n\n` keepalive every 20 seconds. A read that has
 * heard nothing at all, not even a keepalive, for this long is not a slow
 * server: it is a half-open socket, the classic wifi-to-LTE handoff, where
 * the OS still thinks the connection is open so nothing else here notices
 * it is dead. More than double the keepalive interval so one delayed frame
 * does not trip it, but still well inside the server's own grace period.
 */
const IDLE_TIMEOUT_MS = 45_000;

/**
 * Opens the stream and reconnects on its own when it drops, because a
 * stream that dies silently is worse than no stream: on this feature
 * specifically, the open stream is the only thing telling the server this
 * end is still here, so losing it without reconnecting means the server's
 * grace period runs out and revokes a pairing nobody actually left.
 *
 * The wait between attempts doubles up to thirty seconds and is jittered
 * (`wait * (0.5 + Math.random())`), so a server that just came back up (for
 * example after a routine deploy that dropped every open connection at
 * once) does not get a reconnect attempt from every affected viewer in the
 * same instant, which would just knock it back down.
 *
 * Returns the function that closes it, which the caller must run.
 */
export function openSecondScreenStream(opts: SecondScreenStreamOptions): () => void {
  let closed = false;
  let attempt = 0;
  let controller: AbortController | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  /*
   * Whether this run has already spent its one renewal.
   *
   * Reset after every successful connection, so a long session can survive
   * many expiries, but a token that comes back 401 immediately after being
   * renewed is genuinely dead and goes terminal on the next pass.
   */
  let renewed = false;

  const connect = async (): Promise<void> => {
    if (closed) return;
    controller = new AbortController();

    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    try {
      const res = await fetch(opts.url, {
        headers: {
          accept: "text/event-stream",
          Authorization: `Bearer ${opts.getJwt()}`,
        },
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        /*
         * A 401, a 403 or a 404 is an answer, not a hiccup: the token this
         * session was redeemed with has expired or been revoked (401), this
         * client is not allowed here (403), or there is nothing here to
         * watch any more (404). Retrying would ask the same question every
         * thirty seconds for as long as the page is open, and would do it
         * against a session that is never coming back.
         */
        /*
         * A 401 gets one chance to be an expiry rather than an ending. 403 and
         * 404 never do: no token renews permission that was refused, or a
         * session that is gone.
         */
        if (res.status === 401 && !renewed && opts.refreshAuth) {
          renewed = true;
          const token = await opts.refreshAuth();
          if (token && !closed) {
            opts.onConnectedChange?.(false);
            // Straight back round, not through the backoff: nothing is wrong
            // with the server, this client was simply carrying a stale token.
            void connect();
            return;
          }
        }

        if (res.status === 401 || res.status === 403 || res.status === 404) {
          opts.onConnectedChange?.(false);
          opts.onTerminal?.(res.status);
          return;
        }
        throw new Error(`stream failed: ${res.status}`);
      }

      opts.onConnectedChange?.(true);
      attempt = 0;
      // This connection worked, so the next expiry gets its own renewal.
      renewed = false;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      // Armed before the first read and re-armed after every read that
      // resolves (data or keepalive alike), so it only ever fires on a read
      // that has genuinely heard nothing for IDLE_TIMEOUT_MS.
      const armIdleTimer = () => {
        if (idleTimer) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => controller?.abort(), IDLE_TIMEOUT_MS);
      };
      armIdleTimer();

      for (;;) {
        const { done, value } = await reader.read();
        armIdleTimer();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Frames end at a blank line; whatever follows the last one is a
        // partial frame and waits here for the rest of itself.
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";

        for (const frame of frames) {
          const name = /^event: (.+)$/m.exec(frame)?.[1];
          const data = /^data: (.+)$/m.exec(frame)?.[1];
          // A frame with no event name is the server's keepalive.
          if (!name || !data) continue;
          try {
            opts.onEvent({ event: name, data: JSON.parse(data) });
          } catch {
            // One unreadable frame is not worth dropping the connection over.
          }
        }
      }
    } catch {
      // Any failure is the same failure from here: the stream is not up.
    } finally {
      if (idleTimer) clearTimeout(idleTimer);
    }

    opts.onConnectedChange?.(false);
    if (closed) return;

    const wait = Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS);
    attempt += 1;
    retryTimer = setTimeout(() => {
      void connect();
    }, wait * (0.5 + Math.random()));
  };

  void connect();

  return () => {
    closed = true;
    if (retryTimer) clearTimeout(retryTimer);
    // Ends the request, which is what tells the server this end left. The
    // alternative is waiting for the grace period to lapse, which reads on
    // the other end as this side lingering long after it actually went away.
    controller?.abort();
  };
}
